import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { store } from '../../store';
import { updateProfile, sendPhoneVerificationCode, verifyPhoneCode } from '../../store/slices/authSlice';
import Input from '../UI/Input';
import Textarea from '../UI/Textarea';
import DateInput from '../UI/DateInput';
import Select from '../UI/Select';
import PhoneInput from '../UI/PhoneInput';
import AvatarUpload from './AvatarUpload';
import PhoneVerification from './PhoneVerification';
import Button from '../UI/Button';
import FormSection from '../UI/FormSection';
import { api } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

interface ProfileFormProps {
  onSave?: () => void;
  onCancel?: () => void;
  className?: string;
}

const TITLES = [
  { value: '', label: 'Select title' },
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
];

const ProfileForm: React.FC<ProfileFormProps> = ({
  onSave,
  onCancel,
  className = '',
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [parishes, setParishes] = useState<Array<{ value: string; label: string }>>([]);
  const [isLoadingParishes, setIsLoadingParishes] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [originalPhone, setOriginalPhone] = useState('');
  const [phoneChanged, setPhoneChanged] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    nickname: '',
    displayName: '',
    bio: '',
    country: 'Antigua and Barbuda',
    parish: '',
    dateOfBirth: '',
    phone: '',
    phoneCountryCode: '+1',
    phoneVerified: false,
    avatar: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load parishes
  useEffect(() => {
    const loadParishes = async () => {
      setIsLoadingParishes(true);
      try {
        const data = await api.parishes.getAll();
        setParishes([
          { value: '', label: 'Select parish' },
          ...data.map((p) => ({ value: p.name, label: p.name })),
        ]);
      } catch (error) {
        console.error('Error loading parishes:', error);
      } finally {
        setIsLoadingParishes(false);
      }
    };
    loadParishes();
  }, []);

  // Initialize form data from user profile
  useEffect(() => {
    if (user?.profile) {
      const profile = user.profile;
      const phone = profile.phone || '';
      const countryCode = phone.startsWith('+1') ? '+1' : phone.match(/^\+\d+/)?.[0] || '+1';
      const phoneNumber = phone.replace(/^\+\d+\s*/, '');
      
      setFormData({
        title: profile.title || '',
        firstName: profile.firstName || '',
        middleName: profile.middleName || '',
        lastName: profile.lastName || '',
        nickname: profile.nickname || '',
        displayName: profile.displayName || '',
        bio: profile.bio || '',
        country: profile.country || 'Antigua and Barbuda',
        parish: profile.parish || '',
        dateOfBirth: profile.dateOfBirth || '',
        phone: phone,
        phoneCountryCode: countryCode,
        phoneVerified: profile.phoneVerified || false,
        avatar: profile.avatar || '',
      });
      setOriginalPhone(phone);
    }
  }, [user]);

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate displayName from name parts
      if (field === 'firstName' || field === 'middleName' || field === 'lastName') {
        const parts = [
          updated.firstName,
          updated.middleName,
          updated.lastName,
        ].filter(Boolean);
        updated.displayName = parts.join(' ') || updated.nickname || '';
      }
      
      // Track phone changes
      if (field === 'phone') {
        setPhoneChanged(value !== originalPhone);
        if (value !== originalPhone) {
          updated.phoneVerified = false;
        }
      }
      
      return updated;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneChange = (fullPhone: string, countryCode: string) => {
    handleChange('phone', fullPhone);
    handleChange('phoneCountryCode', countryCode);
  };

  const handleAvatarUpload = async (file: File) => {
    // Get access token from Redux store
    const state = store.getState();
    const token = state.auth.accessToken;
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    // Upload file to server
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    const response = await axios.post(
      `${API_BASE_URL}/files/upload`,
      uploadFormData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const uploadedFile = response.data.data.file;
    // Get full URL (assuming the API returns a relative path)
    const avatarUrl = uploadedFile.thumbnailUrl;
    
    // Update form data
    handleChange('avatar', avatarUrl);
    
    // Immediately save avatar to profile
    try {
      await dispatch(updateProfile({ avatar: avatarUrl })).unwrap();
    } catch (error: any) {
      console.error('Failed to save avatar:', error);
      throw new Error('Failed to save avatar to profile');
    }
  };

  const handleVerifyPhone = async () => {
    // Send verification code first
    try {
      await dispatch(sendPhoneVerificationCode(formData.phone)).unwrap();
      setShowPhoneVerification(true);
    } catch (error: any) {
      setErrors({ phone: error.message || 'Failed to send verification code' });
    }
  };

  const handlePhoneVerify = async (code: string) => {
    try {
      await dispatch(verifyPhoneCode({ phoneNumber: formData.phone, code })).unwrap();
      setFormData(prev => ({ ...prev, phoneVerified: true }));
      setPhoneChanged(false);
      setOriginalPhone(formData.phone);
      setShowPhoneVerification(false);
    } catch (error: any) {
      throw new Error(error.message || 'Verification failed');
    }
  };

  const handlePhoneResend = async () => {
    try {
      await dispatch(sendPhoneVerificationCode(formData.phone)).unwrap();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to resend code');
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (formData.phone && !formData.phoneVerified && phoneChanged) {
      newErrors.phone = 'Phone number must be verified before saving';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If phone changed, require verification
    if (phoneChanged && !formData.phoneVerified) {
      setErrors({ phone: 'Please verify your phone number before saving' });
      setShowPhoneVerification(true);
      return;
    }
    
    if (!validate()) {
      return;
    }

    setIsLoading(true);
    try {
      const profileData = {
        title: formData.title,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        nickname: formData.nickname,
        displayName: formData.displayName,
        bio: formData.bio,
        country: formData.country,
        parish: formData.parish,
        dateOfBirth: formData.dateOfBirth,
        phone: formData.phone,
        phoneVerified: formData.phoneVerified,
        avatar: formData.avatar,
      };
      
      await dispatch(updateProfile(profileData)).unwrap();
      setIsEditing(false);
      setErrors({});
      setPhoneChanged(false);
      setOriginalPhone(formData.phone);
      onSave?.();
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to update profile' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    if (user?.profile) {
      const profile = user.profile;
      const phone = profile.phone || '';
      const countryCode = phone.startsWith('+1') ? '+1' : phone.match(/^\+\d+/)?.[0] || '+1';
      
      setFormData({
        title: profile.title || '',
        firstName: profile.firstName || '',
        middleName: profile.middleName || '',
        lastName: profile.lastName || '',
        nickname: profile.nickname || '',
        displayName: profile.displayName || '',
        bio: profile.bio || '',
        country: profile.country || 'Antigua and Barbuda',
        parish: profile.parish || '',
        dateOfBirth: profile.dateOfBirth || '',
        phone: phone,
        phoneCountryCode: countryCode,
        phoneVerified: profile.phoneVerified || false,
        avatar: profile.avatar || '',
      });
    }
    setIsEditing(false);
    setErrors({});
    setPhoneChanged(false);
    setOriginalPhone(formData.phone);
    onCancel?.();
  };

  if (!isEditing) {
    return (
      <FormSection title="Profile Information" className={className}>
        <div className="space-y-6">
          <AvatarUpload
            onUpload={handleAvatarUpload}
            currentAvatarUrl={formData.avatar}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Title
              </label>
              <p className="text-[var(--color-default-font)]">
                {formData.title || <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Full Name
              </label>
              <p className="text-[var(--color-default-font)]">
                {[formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ') || 
                 <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Nickname
              </label>
              <p className="text-[var(--color-default-font)]">
                {formData.nickname || <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Date of Birth
              </label>
              <p className="text-[var(--color-default-font)]">
                {formData.dateOfBirth ? new Date(formData.dateOfBirth).toLocaleDateString() : 
                 <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Country
              </label>
              <p className="text-[var(--color-default-font)]">
                {formData.country || <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Parish
              </label>
              <p className="text-[var(--color-default-font)]">
                {formData.parish || <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Phone
              </label>
              <div className="flex items-center gap-2">
                <p className="text-[var(--color-default-font)]">
                  {formData.phone || <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
                </p>
                {formData.phone && (
                  <span className={`text-xs px-2 py-1 rounded ${
                    formData.phoneVerified 
                      ? 'bg-[var(--color-success-100)] text-[var(--color-success-700)]' 
                      : 'bg-[var(--color-warning-100)] text-[var(--color-warning-700)]'
                  }`}>
                    {formData.phoneVerified ? 'Verified' : 'Unverified'}
                  </span>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Bio
              </label>
              <p className="text-[var(--color-default-font)] whitespace-pre-wrap">
                {formData.bio || <span className="text-[var(--color-neutral-400)] italic">Not set</span>}
              </p>
            </div>
          </div>
          
          <div className="pt-2">
            <Button type="button" onClick={() => setIsEditing(true)}>
              Edit Profile
            </Button>
          </div>
        </div>
      </FormSection>
    );
  }

  return (
    <>
      <FormSection title="Profile Information" className={className}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <AvatarUpload
            onUpload={handleAvatarUpload}
            currentAvatarUrl={formData.avatar}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              options={TITLES}
              fullWidth
              error={errors.title}
            />
            
            <Input
              label="Nickname"
              value={formData.nickname}
              onChange={(e) => handleChange('nickname', e.target.value)}
              fullWidth
              error={errors.nickname}
            />
            
            <Input
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              fullWidth
              error={errors.firstName}
              required
            />
            
            <Input
              label="Middle Name"
              value={formData.middleName}
              onChange={(e) => handleChange('middleName', e.target.value)}
              fullWidth
              error={errors.middleName}
            />
            
            <Input
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              fullWidth
              error={errors.lastName}
              required
            />
            
            <DateInput
              label="Date of Birth"
              value={formData.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              fullWidth
              error={errors.dateOfBirth}
              max={new Date().toISOString().split('T')[0]}
            />
            
            <Input
              label="Country"
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              fullWidth
              error={errors.country}
            />
            
            <Select
              label="Parish"
              value={formData.parish}
              onChange={(e) => handleChange('parish', e.target.value)}
              options={parishes}
              fullWidth
              error={errors.parish}
              disabled={isLoadingParishes}
            />
          </div>

          <PhoneInput
            value={formData.phone}
            onChange={handlePhoneChange}
            countryCode={formData.phoneCountryCode}
            label="Phone Number"
            error={errors.phone}
            fullWidth
            verified={formData.phoneVerified && !phoneChanged}
            onVerify={handleVerifyPhone}
          />

          <Textarea
            label="Bio"
            value={formData.bio}
            onChange={(e) => handleChange('bio', e.target.value)}
            fullWidth
            rows={4}
            error={errors.bio}
          />

          {errors.submit && (
            <div className="p-3 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-md">
              <p className="text-sm text-[var(--color-error-600)]">{errors.submit}</p>
            </div>
          )}
          
          <div className="flex gap-2 pt-2">
            <Button type="submit" isLoading={isLoading} disabled={isLoading}>
              Save Changes
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
          </div>
        </form>
      </FormSection>

      {showPhoneVerification && (
        <PhoneVerification
          phoneNumber={formData.phone}
          onVerify={handlePhoneVerify}
          onResend={handlePhoneResend}
        />
      )}
    </>
  );
};

export default ProfileForm;
